import { useEffect, useState } from 'react';
import { Mail, Phone, User, UserCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function DirectorioPanel({ token }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const fotoUrl = (url) => url ? `${API_URL.replace('/api', '')}${url}` : null;

  useEffect(() => {
    fetch(`${API_URL}/usuarios/directorio`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setUsuarios(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleExportPDF = async () => {
    const res = await fetch(`${API_URL}/usuarios/directorio-pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'directorio_usuarios.pdf'; a.click();
    URL.revokeObjectURL(url);
  };

  const iniciales = (nombre) =>
    nombre?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() ?? '?';

  // Agrupa por sucursal y ordena alfabéticamente dentro de cada grupo
  const porSucursal = usuarios.reduce((acc, u) => {
    const key = u.sucursal_nombre ?? 'Sin sucursal';
    if (!acc[key]) acc[key] = [];
    acc[key].push(u);
    return acc;
  }, {});

  const sucursalesOrdenadas = Object.keys(porSucursal).sort((a, b) => a.localeCompare(b, 'es'));

  if (loading) return <p style={{ fontSize: 13, color: '#888' }}>Cargando directorio...</p>;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Directorio de personal</div>
          <div className="card-subtitle">{usuarios.length} colaboradores activos</div>
        </div>
        <button className="btn-secondary" style={{ fontSize: 12 }} onClick={handleExportPDF}>
          Exportar PDF
        </button>
      </div>

      <div style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
        {sucursalesOrdenadas.map(sucursal => (
          <div key={sucursal} style={{ marginBottom: '1.25rem' }}>
            {/* Encabezado de sucursal */}
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: '#1e6378',
              borderBottom: '2px solid #1e6378',
              paddingBottom: 4, marginBottom: 8
            }}>
              {sucursal}
            </div>

            {/* Grid de credenciales */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: '0.625rem',
            }}>
              {porSucursal[sucursal]
                .sort((a, b) => a.nombre_completo?.localeCompare(b.nombre_completo, 'es'))
                .map(u => (
                  <Credencial key={u.id} u={u} fotoUrl={fotoUrl} iniciales={iniciales} />
                ))
              }
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Credencial({ u, fotoUrl, iniciales }) {
  const foto = fotoUrl(u.foto_url);
  const nombreCompleto = [u.titulo, u.nombre_completo]
    .filter(Boolean)
    .join(" ");

  const color = "#0D9AA5";
  const gris = "#8A8D91";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#fff",
        borderRadius: 8,
        border: "1px solid #ddd",
        overflow: "hidden",
        height: 126,
        padding: "12px 14px"
      }}
    >
      {/* FOTO */}
      <div
        style={{
          width: 92,
          height: 92,
          background: "#94979B",
          borderRadius: 12,
          overflow: "hidden",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {foto ? (
          <img
            src={foto}
            alt={nombreCompleto}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }}
          />
        ) : (
          <UserCircle2
            size={72}
            color={color}
          />
        )}
      </div>

      {/* LÍNEA DIVISORA */}
      <div
        style={{
          width: 6,
          height: 92,
          background: color,
          margin: "0 16px"
        }}
      />

      {/* DATOS */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 5,
          overflow: "hidden"
        }}
      >
        <Fila
          titulo="NOMBRE:"
          valor={nombreCompleto}
          color={color}
          gris={gris}
        />

        <Fila
          titulo="ÁREA:"
          valor={u.area_nombre || "-"}
          color={color}
          gris={gris}
        />

        {/* TELÉFONO */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}
        >
          {/* Círculo Gris con Teléfono Blanco */}
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: gris,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <Phone
              size={11}
              color="#ffffff"
              style={{ transform: "rotate(0deg)" }}
            />
          </div>

          <span
            style={{
              color,
              fontWeight: 700,
              fontSize: 15
            }}
          >
            {u.num_corp || "-"}
          </span>
        </div>

        <Fila
          titulo="EXT:"
          valor={u.tel_ext || "-"}
          color={color}
          gris={gris}
        />

        {/* CORREO */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0
          }}
        >
          {/* Círculo Gris con Correo Blanco */}
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: gris,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <Mail
              size={11}
              color="#ffffff"
            />
          </div>

          <span
            style={{
              color,
              fontWeight: 700,
              fontSize: 14,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {u.email}
          </span>
        </div>
      </div>
    </div>
  );
}

function Fila({ titulo, valor, color, gris }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 6,
        overflow: "hidden"
      }}
    >
      <span
        style={{
          color: gris,
          fontWeight: 800,
          fontSize: 14,
          flexShrink: 0
        }}
      >
        {titulo}
      </span>

      <span
        style={{
          color,
          fontWeight: 800,
          fontSize: 15,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {valor}
      </span>
    </div>
  );
}